import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CrosshairMode, LastPriceAnimationMode } from 'lightweight-charts';
import { FiChevronRight, FiSearch, FiBarChart2, FiTrendingUp, FiCrosshair } from 'react-icons/fi';
import { URL_API, WS_BASE } from '../config/endpoints';
import { useSettings } from '../settings';

const iconPath = (sym) => `/access/coinIcon/${String(sym || '').toLowerCase()}.png`;
const TFS = ['1m', '5m', '10m', '15m'];
const CHART_H = 416;

// Призрачные свечи для скелетона загрузки — детерминированный «случайный» путь цены,
// плавающие на разной высоте (как настоящий график), а не столбики от низа.
const SKELETON_CANDLES = (() => {
  const arr = [];
  let p = 50;
  for (let i = 0; i < 26; i++) {
    const delta = Math.sin(i * 0.7) * 6 + Math.sin(i * 1.9) * 3.4 + Math.cos(i * 0.4) * 4;
    const o = p;
    const c = Math.max(14, Math.min(86, p + delta));
    const hi = Math.min(96, Math.max(o, c) + 3 + Math.abs(Math.sin(i * 1.3)) * 4);
    const lo = Math.max(4, Math.min(o, c) - 3 - Math.abs(Math.cos(i * 1.1)) * 4);
    arr.push({ o, c, hi, lo });
    p = c;
  }
  return arr;
})();

export default function TradeChart({ tokens, defaultSymbol = 'BTC', onSymbolChange, onPriceUpdate }) {
  const onPriceRef = useRef(onPriceUpdate);
  useEffect(() => { onPriceRef.current = onPriceUpdate; }, [onPriceUpdate]);
  const reportPrice = useCallback((p) => { try { onPriceRef.current && onPriceRef.current(p); } catch {} }, []);
  const { effectiveTz } = useSettings();
  const tzRef = useRef(effectiveTz);
  useEffect(() => { tzRef.current = effectiveTz; }, [effectiveTz]);
  const wrapRef = useRef(null);
  const ddWrapRef = useRef(null); // обёртка селектора токена — для закрытия по клику вне
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const roRef = useRef(null);
  const refOpenRef = useRef(null);
  const lastPriceRef = useRef(null);
  const wsRef = useRef(null);
  const typeRef = useRef('candles');
  const symbolRef = useRef(defaultSymbol);
  const tfRef = useRef('1m');
  const pressRef = useRef({ x: 0, y: 0, pressed: false }); // отличаем тап от перетаскивания/зума
  const movedRef = useRef(false);

  const [symbol, setSymbol] = useState(defaultSymbol);
  const [tf, setTf] = useState('1m');
  const [type, setType] = useState('candles');
  const [ddOpen, setDdOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [last, setLast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [barInfo, setBarInfo] = useState(null); // выбранная свеча (тап) → панель O/H/L/C
  const [autoFit, setAutoFit] = useState(true);  // авто-подгон вертикального масштаба (как TradingView)

  // USDT — котировочная валюта (все пары XXX/USDT), её саму торговать нельзя → исключаем
  const tokenKeys = Object.keys(tokens || {}).filter((k) => k !== 'trading-pairs' && String(k).toUpperCase() !== 'USDT');
  const filtered = tokenKeys.filter((k) => !search || k.toLowerCase().includes(search.toLowerCase()) || (tokens?.[k]?.title || '').toLowerCase().includes(search.toLowerCase()));

  // создать график один раз
  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const tzOpt = () => (tzRef.current ? { timeZone: tzRef.current } : {});
    const fmtLocal = (t) => { try { return new Date(t * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', ...tzOpt() }); } catch { return new Date(t * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); } };
    const chart = createChart(el, {
      width: el.clientWidth,
      height: CHART_H,
      layout: { background: { color: 'transparent' }, textColor: '#8A90A0', fontSize: 11, attributionLogo: false },
      grid: { vertLines: { color: 'rgba(255,255,255,0.04)' }, horzLines: { color: 'rgba(255,255,255,0.04)' } },
      rightPriceScale: { borderColor: '#2A2A33', scaleMargins: { top: 0.04, bottom: 0.06 } },
      timeScale: {
        borderColor: '#2A2A33', timeVisible: true, secondsVisible: false,
        rightOffset: 6, barSpacing: 8, fixLeftEdge: true, fixRightEdge: false,
        lockVisibleTimeRangeOnResize: true,
        tickMarkFormatter: (t) => fmtLocal(t),  // локальная таймзона на оси
      },
      localization: { timeFormatter: (t) => { try { return new Date(t * 1000).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short', ...tzOpt() }); } catch { return new Date(t * 1000).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }); } } },
      crosshair: { mode: CrosshairMode.Normal, vertLine: { color: '#3A3A46', labelBackgroundColor: '#2A2A33' }, horzLine: { color: '#3A3A46', labelBackgroundColor: '#2A2A33' } },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: true },
      handleScale: { axisPressedMouseMove: { time: true, price: true }, axisDoubleClickReset: { time: true, price: true }, mouseWheel: true, pinch: true },
    });
    chartRef.current = chart;
    roRef.current = new ResizeObserver(() => { if (chartRef.current && el) chartRef.current.applyOptions({ width: el.clientWidth }); });
    roRef.current.observe(el);

    // Тап по свече → панель O/H/L/C. Игнорируем, если палец двигали (скролл/зум графика).
    const onDown = (e) => { pressRef.current = { x: e.clientX, y: e.clientY, pressed: true }; movedRef.current = false; };
    const onMove = (e) => {
      const p = pressRef.current;
      if (p.pressed && (Math.abs(e.clientX - p.x) + Math.abs(e.clientY - p.y)) > 7) movedRef.current = true;
    };
    const onUp = () => { pressRef.current.pressed = false; };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);

    const onClick = (param) => {
      if (movedRef.current) return;                 // это был драг/зум, не тап
      const s = seriesRef.current;
      if (!param || param.time == null || !s) { setBarInfo(null); return; }
      const d = param.seriesData ? param.seriesData.get(s) : null;
      if (!d) { setBarInfo(null); return; }
      const o = d.open ?? d.value, h = d.high ?? d.value, l = d.low ?? d.value, c = d.close ?? d.value;
      setBarInfo({ time: param.time, o, h, l, c });
    };
    chart.subscribeClick(onClick);

    return () => {
      try { el.removeEventListener('pointerdown', onDown); el.removeEventListener('pointermove', onMove); el.removeEventListener('pointerup', onUp); el.removeEventListener('pointercancel', onUp); } catch {}
      try { chart.unsubscribeClick(onClick); } catch {}
      try { roRef.current && roRef.current.disconnect(); } catch {}
      try { chart.remove(); } catch {}
      chartRef.current = null; seriesRef.current = null;
    };
  }, []);

  // Перерисовать ось/курсор графика при смене часового пояса (DST учитывается IANA-зоной)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const tzo = effectiveTz ? { timeZone: effectiveTz } : {};
    const tickFmt = (t) => { try { return new Date(t * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', ...tzo }); } catch { return new Date(t * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); } };
    const crossFmt = (t) => { try { return new Date(t * 1000).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short', ...tzo }); } catch { return new Date(t * 1000).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }); } };
    try { chart.applyOptions({ timeScale: { tickMarkFormatter: tickFmt }, localization: { timeFormatter: crossFmt } }); } catch (e) {}
  }, [effectiveTz]);

  // загрузка + (пере)создание серии
  const load = useCallback(async () => {
    const chart = chartRef.current;
    if (!chart) return;
    setLoading(true);
    let candles = [];
    try {
      const res = await fetch(`${URL_API}/candles?symbol=${encodeURIComponent(symbol)}&tf=${tf}&limit=1440`);
      const data = await res.json();
      candles = Array.isArray(data?.candles) ? data.candles : [];
    } catch { candles = []; }

    try { if (seriesRef.current) chart.removeSeries(seriesRef.current); } catch {}
    if (type === 'candles') {
      seriesRef.current = chart.addCandlestickSeries({ upColor: '#3FD18B', downColor: '#FF6B6B', borderUpColor: '#3FD18B', borderDownColor: '#FF6B6B', wickUpColor: '#3FD18B', wickDownColor: '#FF6B6B' });
      seriesRef.current.setData(candles.map((c) => ({ time: c.t, open: c.o, high: c.h, low: c.l, close: c.c })));
    } else {
      seriesRef.current = chart.addLineSeries({ color: '#60BDFF', lineWidth: 2, priceLineVisible: true, lastValueVisible: true, lastPriceAnimation: LastPriceAnimationMode.Continuous });
      seriesRef.current.setData(candles.map((c) => ({ time: c.t, value: c.c })));
    }

    if (candles.length) {
      const lastC = candles[candles.length - 1];
      const firstC = candles[Math.max(0, candles.length - 70)];
      try { chart.timeScale().setVisibleRange({ from: firstC.t, to: lastC.t }); } catch {}
      refOpenRef.current = candles[0]?.o ?? lastC.o;
      lastPriceRef.current = lastC.c;
      const base = refOpenRef.current || lastC.o;
      setLast({ price: lastC.c, changePct: base ? ((lastC.c - base) / base) * 100 : 0 });
      reportPrice(lastC.c);
    } else { setLast(null); lastPriceRef.current = null; reportPrice(null); }
    setLoading(false);
  }, [symbol, tf, type, reportPrice]);

  useEffect(() => { load(); }, [load]);

  // синхронизируем рефы с состоянием (для WS-обработчика)
  useEffect(() => { symbolRef.current = symbol; tfRef.current = tf; typeRef.current = type; }, [symbol, tf, type]);

  // сообщаем родителю выбранный символ (для торговой панели под графиком)
  useEffect(() => { try { onSymbolChange && onSymbolChange(symbol); } catch {} }, [symbol, onSymbolChange]);

  // закрытие дропдауна токена кликом в любом месте вне его
  useEffect(() => {
    if (!ddOpen) return;
    const onDown = (e) => { if (ddWrapRef.current && !ddWrapRef.current.contains(e.target)) { setDdOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [ddOpen]);

  // живое обновление через WebSocket (~1с), одно соединение, смена подписки сообщением
  useEffect(() => {
    let stopped = false;
    let reconnectT = null;

    const applyCandle = (c) => {
      const s = seriesRef.current;
      if (!s || !c) return;
      try {
        if (typeRef.current === 'candles') s.update({ time: c.t, open: c.o, high: c.h, low: c.l, close: c.c });
        else s.update({ time: c.t, value: c.c });
      } catch {}
      lastPriceRef.current = c.c;
      const base = refOpenRef.current || c.o;
      setLast({ price: c.c, changePct: base ? ((c.c - base) / base) * 100 : 0 });
      reportPrice(c.c);
    };

    const connect = () => {
      if (stopped) return;
      let ws;
      try {
        ws = new WebSocket(`${WS_BASE}/ws/candles?symbol=${encodeURIComponent(symbolRef.current)}&tf=${tfRef.current}`);
      } catch { reconnectT = setTimeout(connect, 2000); return; }
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try { const m = JSON.parse(ev.data); if (m && m.type === 'candle') applyCandle(m.candle); } catch {}
      };
      ws.onclose = () => { wsRef.current = null; if (!stopped) reconnectT = setTimeout(connect, 2000); };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };
    connect();

    return () => { stopped = true; if (reconnectT) clearTimeout(reconnectT); try { wsRef.current && wsRef.current.close(); } catch {} wsRef.current = null; };
  }, []);

  // смена символа/таймфрейма — переподписка через тот же WS (без реконнекта)
  useEffect(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      try { ws.send(JSON.stringify({ type: 'sub', symbol, tf })); } catch {}
    }
  }, [symbol, tf]);

  // синхронизируем кнопку авто-фита с реальным состоянием шкалы: LWC выключает
  // autoScale, когда пользователь вручную тянет ценовую ось → кнопка гаснет сама.
  useEffect(() => {
    const iv = setInterval(() => {
      const ch = chartRef.current;
      if (!ch) return;
      try {
        const on = !!ch.priceScale('right').options().autoScale;
        setAutoFit((prev) => (prev === on ? prev : on));
      } catch {}
    }, 350);
    return () => clearInterval(iv);
  }, []);

  // тумблер: включает авто-подгон вертикали (сам масштабируется при листании)
  const toggleAutoFit = useCallback(() => {
    const ch = chartRef.current;
    if (!ch) return;
    try {
      const cur = !!ch.priceScale('right').options().autoScale;
      ch.priceScale('right').applyOptions({ autoScale: !cur });
      setAutoFit(!cur);
    } catch {}
  }, []);

  const title = tokens?.[symbol]?.title || symbol;
  const up = (last?.changePct ?? 0) >= 0;
  const fmtPrice = (p) => {
    if (p == null) return '—';
    if (p >= 1000) return p.toLocaleString('en-US', { maximumFractionDigits: 2 });
    if (p >= 1) return p.toFixed(2);
    if (p >= 0.01) return p.toFixed(4);
    return p.toPrecision(4);
  };

  return (
    <div>
      {/* Токен + цена/процент */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ position: 'relative' }} ref={ddWrapRef}>
          <div onClick={() => { setDdOpen((v) => !v); setSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 12, padding: '6px 11px' }}>
            <img src={iconPath(symbol)} alt={symbol} style={{ width: 20, height: 20, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <span style={{ color: '#EAF6FF', fontWeight: 700, fontSize: 14 }}>{symbol}<span style={{ color: '#9FB6CC', fontWeight: 500, fontSize: 11 }}> /USDT</span></span>
            <FiChevronRight size={14} color="#FFFFFF" style={{ transform: ddOpen ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s ease' }} />
          </div>
          {ddOpen && (
            <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 40, width: 230, background: '#16161B', border: '1px solid #2A2A33', borderRadius: 14, boxShadow: '0 14px 34px rgba(0,0,0,0.5)', overflow: 'hidden', animation: 'walletIn 0.2s ease both' }}>
              <div style={{ position: 'relative', padding: 8, borderBottom: '1px solid #2A2A33' }}>
                <FiSearch size={14} color="#7E8190" style={{ position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)' }} />
                <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" style={{ width: '100%', boxSizing: 'border-box', background: '#2D2D33', border: '1px solid #3E3E4B', borderRadius: 10, outline: 'none', color: '#fff', padding: '9px 10px 9px 32px', fontSize: 13 }} />
              </div>
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                {filtered.map((k) => (
                  <div key={k} onClick={() => { setSymbol(k); setDdOpen(false); setSearch(''); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <img src={iconPath(k)} alt={k} style={{ width: 26, height: 26, borderRadius: '50%' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{k}</span>
                    <span style={{ color: '#7E8190', fontSize: 12, marginLeft: 'auto' }}>{tokens?.[k]?.title || ''}</span>
                  </div>
                ))}
                {filtered.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: '#6A6A78', fontSize: 13 }}>No tokens</div>}
              </div>
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: '#fff', fontWeight: 700, fontSize: 17, letterSpacing: '-0.2px', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>${fmtPrice(last?.price)}</div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 3, background: up ? 'rgba(63,209,139,0.12)' : 'rgba(255,107,107,0.12)', borderRadius: 7, padding: '1px 7px' }}>
            <span style={{ color: up ? '#3FD18B' : '#FF6B6B', fontSize: 11.5, fontWeight: 700 }}>{up ? '▲' : '▼'} {Math.abs(last?.changePct ?? 0).toFixed(2)}%</span>
          </div>
        </div>
      </div>

      {/* Контролы: таймфрейм (раскрытие ВПРАВО) + тип */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, minHeight: 34 }}>
        <div style={{ display: 'inline-flex', background: 'rgba(0,0,0,0.25)', borderRadius: 10, padding: 3, gap: 2 }}>
          {TFS.map((t) => {
            const on = tf === t;
            return (
              <button key={t} onClick={() => setTf(t)} style={{ border: 'none', cursor: 'pointer', borderRadius: 7, padding: '6px 13px', fontSize: 12.5, fontWeight: 600, background: on ? 'rgba(96,189,255,0.20)' : 'transparent', color: on ? '#EAF6FF' : '#7E8AA0', transition: 'background 0.2s ease, color 0.2s ease' }}>{t}</button>
            );
          })}
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {/* Авто-масштаб вертикали (как в TradingView): вкл → сам подгоняет высоту при листании */}
          <button
            onClick={toggleAutoFit}
            aria-label="Auto scale"
            title={autoFit ? 'Авто-масштаб включён' : 'Авто-масштаб выключен'}
            style={{ border: 'none', cursor: 'pointer', borderRadius: 10, padding: '7px 9px', display: 'flex', alignItems: 'center', background: autoFit ? 'rgba(96,189,255,0.20)' : 'rgba(0,0,0,0.25)', color: autoFit ? '#60BDFF' : '#7E8AA0', transition: 'background 0.2s ease, color 0.2s ease' }}
          >
            <FiCrosshair size={16} />
          </button>
          <div style={{ display: 'inline-flex', background: 'rgba(0,0,0,0.25)', borderRadius: 10, padding: 3, gap: 2 }}>
            {[{ k: 'candles', Icon: FiBarChart2 }, { k: 'line', Icon: FiTrendingUp }].map(({ k, Icon }) => {
              const on = type === k;
              return <button key={k} onClick={() => setType(k)} aria-label={k} style={{ border: 'none', cursor: 'pointer', borderRadius: 7, padding: '6px 12px', display: 'flex', alignItems: 'center', background: on ? 'rgba(96,189,255,0.20)' : 'transparent', color: on ? '#EAF6FF' : '#7E8AA0', transition: 'background 0.2s ease, color 0.2s ease' }}><Icon size={16} /></button>;
            })}
          </div>
        </div>
      </div>

      {/* График */}
      <div style={{ position: 'relative', width: 'calc(100% + 26px)', marginLeft: -13, overflow: 'hidden' }}>
        <div ref={wrapRef} style={{ width: '100%', height: CHART_H }} />
        {loading && (
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <style>{`
              @keyframes tcShim { 0% { transform: translateX(-180%); } 100% { transform: translateX(320%); } }
              @keyframes tcBreath { 0%,100% { opacity:.55 } 50% { opacity:.85 } }
            `}</style>
            {/* сетка как у настоящего графика */}
            <div style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(to bottom, rgba(255,255,255,0.04) 0, rgba(255,255,255,0.04) 1px, transparent 1px, transparent 25%)' }} />
            {/* призрачные свечи, плавающие на разной высоте + фитили */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'stretch', gap: 5, padding: '20px 14px 34px', animation: 'tcBreath 1.7s ease-in-out infinite' }}>
              {SKELETON_CANDLES.map((k, i) => {
                const wTop = 100 - k.hi, wH = Math.max(k.hi - k.lo, 1);
                const bTop = 100 - Math.max(k.o, k.c), bH = Math.max(Math.abs(k.c - k.o), 2);
                return (
                  <div key={i} style={{ position: 'relative', flex: 1 }}>
                    <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: wTop + '%', height: wH + '%', width: 1.5, background: 'rgba(255,255,255,0.06)', borderRadius: 1 }} />
                    <div style={{ position: 'absolute', left: '16%', right: '16%', top: bTop + '%', height: bH + '%', background: 'rgba(255,255,255,0.08)', borderRadius: 2 }} />
                  </div>
                );
              })}
            </div>
            {/* бегущий блик (shimmer) */}
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: '42%', background: 'linear-gradient(100deg, transparent 0%, rgba(96,189,255,0.08) 50%, transparent 100%)', animation: 'tcShim 1.6s ease-in-out infinite' }} />
          </div>
        )}
      </div>
    </div>
  );
}
